import { useState } from 'react';
import { motion } from 'framer-motion';
import { LogIn, UserPlus, AlertCircle, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { pb } from '../../lib/pocketbase';
import { useNavigate } from 'react-router-dom';
import Logo from '../../components/common/Logo';

export default function PartnerLogin() {
    const [isLogin, setIsLogin] = useState(true);
    const [isResetting, setIsResetting] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resetMsg, setResetMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setResetMsg('');
        setIsLoading(true);

        try {
            if (isResetting) {
                if (!email) throw new Error("Bitte gib deine E-Mail-Adresse ein");
                await pb.collection('users').requestPasswordReset(email);
                setResetMsg("Ein Link zum Zurücksetzen wurde an deine E-Mail gesendet.");
                setIsResetting(false);
                setIsLogin(true);
            } else if (isLogin) {
                // Login
                await pb.collection('users').authWithPassword(email, password);
                navigate('/partner-dashboard');
            } else {
                // Registration
                if (password !== passwordConfirm) {
                    throw new Error("Passwörter stimmen nicht überein");
                }
                if (password.length < 8) {
                    throw new Error("Passwort muss mindestens 8 Zeichen lang sein");
                }

                // Create user
                await pb.collection('users').create({
                    email,
                    password,
                    passwordConfirm,
                    emailVisibility: true,
                    // defaults for our custom fields
                    affiliateRate: 0,
                    totalReferrals: 0
                });

                // Auto-login after registration
                await pb.collection('users').authWithPassword(email, password);
                navigate('/partner-dashboard');
            }
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Ein Fehler ist aufgetreten. Bitte überprüfe deine Eingaben.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col justify-center px-4 sm:px-6 lg:px-8 font-sans">
            <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8">
                <div className="flex justify-center">
                    <Logo />
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-[#0F172A]" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                    Partner-Portal
                </h2>
                <p className="mt-2 text-center text-sm text-slate-600">
                    {isResetting ? 'Passwort zurücksetzen' : (isLogin ? 'Melde dich an, um dein Dashboard zu sehen' : 'Erstelle jetzt deinen kostenlosen Partner-Account')}
                </p>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="sm:mx-auto sm:w-full sm:max-w-md"
            >
                <div className="bg-white py-8 px-4 shadow-xl sm:rounded-2xl sm:px-10 border border-slate-100">

                    {/* Tabs */}
                    {!isResetting && (
                        <div className="flex bg-slate-100 p-1 rounded-xl mb-8">
                            <button
                                onClick={() => { setIsLogin(true); setError(null); }}
                                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${isLogin ? 'bg-white text-[#0F172A] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <span className="flex items-center justify-center gap-2">
                                    <LogIn className="w-4 h-4" /> Login
                                </span>
                            </button>
                            <button
                                onClick={() => { setIsLogin(false); setError(null); }}
                                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${!isLogin ? 'bg-white text-[#0F172A] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <span className="flex items-center justify-center gap-2">
                                    <UserPlus className="w-4 h-4" /> Registrieren
                                </span>
                            </button>
                        </div>
                    )}

                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}

                    {resetMsg && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-100 rounded-xl flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-green-600 shrink-0 mt-0.5 text-xl" />
                            <p className="text-sm text-green-800">{resetMsg}</p>
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                E-Mail Adresse
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="appearance-none block w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                                placeholder="name@firma.de"
                            />
                        </div>

                        {!isResetting && (
                            <>
                                <div>
                                    <div className="flex justify-between items-center mb-1.5">
                                        <label className="block text-sm font-medium text-slate-700">
                                            Passwort
                                        </label>
                                        {isLogin && (
                                            <button
                                                type="button"
                                                onClick={() => { setIsResetting(true); setError(null); setResetMsg(''); }}
                                                className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A]"
                                            >
                                                Passwort vergessen?
                                            </button>
                                        )}
                                    </div>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            required={!isResetting}
                                            minLength={8}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="appearance-none block w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all pr-12"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none p-1"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {!isLogin && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                    >
                                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                            Passwort bestätigen
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPasswordConfirm ? "text" : "password"}
                                                required={!isLogin}
                                                minLength={8}
                                                value={passwordConfirm}
                                                onChange={(e) => setPasswordConfirm(e.target.value)}
                                                className="appearance-none block w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all pr-12"
                                                placeholder="••••••••"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none p-1"
                                            >
                                                {showPasswordConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </>
                        )}

                        <div className="flex flex-col gap-3">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-[#0F172A] bg-gradient-to-r from-[#10B981] to-[#1E3A8A] hover:bg-[#00e5ff] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    'Bitte warten...'
                                ) : (
                                    <>
                                        {isResetting ? 'Reset-Link senden' : (isLogin ? 'Anmelden' : 'Account erstellen')}
                                        {!isResetting && <ArrowRight className="w-4 h-4" />}
                                    </>
                                )}
                            </button>

                            {isResetting && (
                                <button
                                    type="button"
                                    onClick={() => { setIsResetting(false); setError(null); }}
                                    className="w-full text-center text-sm font-medium text-slate-500 hover:text-slate-700 py-2"
                                >
                                    Zurück zum Login
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </motion.div>
        </div>
    );
}
